/**
 * RTWO Blockchain Core Foundation — Validation
 * RTWO v2.2 | Module ID: RTWO-BLCK-001
 */

import { BLOCKCHAIN_CORE_VERSION, type BlockchainCoreValidationResult } from '../domain-models';
import {
  isBlockchainCoreBackendEnabled,
  isBlockchainCoreEnabled,
} from '../configuration/blockchain-core-config';
import { getBlockRuntimeService } from '../factory/create-blockchain-core-runtime';

export async function validateBlockchainCore(): Promise<BlockchainCoreValidationResult> {
  const coreEnabled = isBlockchainCoreEnabled();
  const backendConnected = isBlockchainCoreBackendEnabled();
  const service = getBlockRuntimeService();
  const latestBlock = service.getLatestBlock();
  const blockLookup = service.lookupBlock({ height: '0' });

  const checks = [
    {
      id: 'blockchain-core-module',
      label: 'Blockchain core module available',
      category: 'module',
      passed: true,
      note: `${BLOCKCHAIN_CORE_VERSION.moduleId} v${BLOCKCHAIN_CORE_VERSION.version}`,
    },
    {
      id: 'blockchain-core-consensus-readiness',
      label: 'Consensus readiness surfaced truthfully on blockchain core',
      category: 'policy',
      passed:
        !latestBlock.available ||
        typeof latestBlock.consensusEnabled === 'boolean',
      note: latestBlock.consensusEnabled
        ? 'Consensus network ready'
        : 'Consensus readiness pending prerequisites',
    },
    {
      id: 'blockchain-core-no-mock-blocks',
      label: 'No mock blocks served',
      category: 'policy',
      passed:
        !latestBlock.available ||
        (latestBlock.block?.blockHash !== null && latestBlock.block?.blockNumber !== null),
      note: 'Truthful Data Policy — Unavailable until real backend exists',
    },
    {
      id: 'blockchain-core-no-fabricated-heights',
      label: 'No fabricated block heights',
      category: 'policy',
      passed: !latestBlock.available || latestBlock.block?.blockNumber !== null,
      note: 'Heights only from connected backend',
    },
    {
      id: 'blockchain-core-no-fake-hashes',
      label: 'No fake block hashes',
      category: 'policy',
      passed: !latestBlock.available || latestBlock.block?.blockHash !== null,
      note: 'Hashes only from connected backend',
    },
    {
      id: 'blockchain-core-latest-block-api',
      label: 'Latest block runtime surface available',
      category: 'runtime',
      passed: latestBlock.moduleId === 'RTWO-BLCK-001',
      note: latestBlock.source,
    },
    {
      id: 'blockchain-core-block-lookup-api',
      label: 'Block lookup runtime surface available',
      category: 'runtime',
      passed: blockLookup.moduleId === 'RTWO-BLCK-001',
      note: blockLookup.source,
    },
    {
      id: 'blockchain-core-unavailable-without-backend',
      label: 'Unavailable without real backend',
      category: 'policy',
      passed: backendConnected || latestBlock.status === 'unavailable',
      note: backendConnected
        ? 'Backend flag enabled — awaiting real indexed blocks'
        : 'Backend disconnected — Unavailable as expected',
    },
  ];

  const blockers: string[] = [];
  if (!coreEnabled) {
    blockers.push('Blockchain core disabled (RTWO_BLOCKCHAIN_CORE_ENABLED=false)');
  }

  const passed = checks.every((check) => check.passed);

  return {
    moduleId: 'RTWO-BLCK-001',
    phase: BLOCKCHAIN_CORE_VERSION.phase,
    passed,
    coreEnabled,
    backendConnected,
    consensusEnabled: latestBlock.consensusEnabled,
    latestBlock,
    blockLookup,
    checks,
    blockers,
    collectedAt: Date.now(),
    source: passed
      ? `${BLOCKCHAIN_CORE_VERSION.moduleName} — Validation Passed (Read Layer · Consensus Readiness Surfaced)`
      : `${BLOCKCHAIN_CORE_VERSION.moduleName} — Validation Failed`,
  };
}
